import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FunctionalTypeFormSelect } from "features/entities/FunctionalTypeFormSelect";
import { InvestorEntitySelect } from "features/investors/AddInvestorEntityRelationshipsModal";
import { useInvestorEntityRelationshipsDestroy } from "gen/endpoints/investor/investor";
import { useInvestorEntityList } from "gen/endpoints/investor-entity/investor-entity";
import { InvestorEntityRelationshipsAPIDataRequest } from "gen/model/investorEntityRelationshipsAPIDataRequest";
import { investorEntityRelationshipsDestroyQueryParams } from "gen/schemas/investor/investor";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";

interface DeleteInvestorEntityRelationshipsModalProps {
  onSuccess?: (args: DeleteInvestorEntityRelationshipsModalArgs) => void;
}

interface DeleteInvestorEntityRelationshipsModalArgs {
  investorId: string;
}

export const DeleteInvestorEntityRelationshipsModal: React.FC<
  ConfirmationDialogProps<
    void,
    DeleteInvestorEntityRelationshipsModalProps,
    DeleteInvestorEntityRelationshipsModalArgs
  >
> = ({
  onSuccess,
  onCancel,
  alertProps,
  extraProps: { onSuccess: onSuccessExtra },
  args,
}) => {
  const { investorId } = args;

  const { t } = useTranslation(["investor"]);

  const form = useForm<InvestorEntityRelationshipsAPIDataRequest>({
    resolver: zodResolver(investorEntityRelationshipsDestroyQueryParams),
    defaultValues: {},
  });

  const { mutateAsync: deleteInvestorEntityRelationships } =
    useInvestorEntityRelationshipsDestroy();

  const { data: investorEntities } = useInvestorEntityList({
    investor_id: investorId,
  });

  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("investor:removeInvestorEntityFromInvestorModal.title")}
        </ModalHeader>
        <Form
          form={form}
          onSubmit={async (data: InvestorEntityRelationshipsAPIDataRequest) => {
            await deleteInvestorEntityRelationships({
              investorId,
              params: { investor_entity_ids: data.investor_entity_ids },
            });
          }}
          onSuccess={() => {
            onSuccess();
            onSuccessExtra?.(args);
          }}
          successMessage={t(
            "investor:removeInvestorEntityFromInvestorModal.success",
          )}
        >
          <ModalBody>
            <FunctionalTypeFormSelect
              isInvestorEntity={true}
              isFundFamilyRelated={false}
            />
            {investorEntities && (
              <InvestorEntitySelect investorEntities={investorEntities} />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={() => onCancel()} />}
              confirm={
                <FormSubmitButton>
                  {t("investor:removeInvestorEntityFromInvestorModal.submit")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
